import React, { useEffect, useState, Suspense, lazy } from 'react';
import { VideoTutorialProvider, useVideoTutorial } from '@/context/VideoTutorialContext';
import { HELP_PANEL_RESERVE_CSS } from '@/constants/helpPanelLayout';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import '@/App.css';
import { config } from '@/config';
import { fetchGranularPermissions } from '@/lib/adminAccessCache';
import Login from '@/pages/Login';
import Home from '@/pages/Home';
import Vitrine from '@/pages/Vitrine';
import Maintenance from '@/pages/Maintenance';
import HelpTutorialWindow from '@/pages/HelpTutorialWindow';
import HelpPanel from '@/components/HelpPanel';
import { Toaster } from '@/components/ui/sonner';
import { AuthProvider, useAuth } from '@/context/AuthContext';
import { ThemeProvider } from '@/context/ThemeContext';
import { GroupProvider } from '@/context/GroupContext';
import { DbModeProvider } from '@/context/DbModeContext';
import DevModeBanner from '@/components/DevModeBanner';
import DevBadge from '@/components/DevBadge';
import WelcomeModal from '@/components/WelcomeModal';
import VersionWhatsNewModal from '@/components/VersionWhatsNewModal';
import PendingDocumentsModal from '@/components/PendingDocumentsModal';
import NewsSidebar from '@/components/NewsSidebar';
import { openPrysmeAccessApp } from '@/utils/prysmeAccessApp';

/** Pages admin : chargées à la demande (hors bundle initial Home/Login). */
const AdminLayout = lazy(() => import('@/components/AdminLayout'));
const AdminUsers = lazy(() => import('@/pages/admin/AdminUsers'));
const AdminServices = lazy(() => import('@/pages/admin/AdminServices'));
const AdminCompanies = lazy(() => import('@/pages/admin/AdminCompanies'));
const AdminApplications = lazy(() => import('@/pages/admin/AdminApplications'));
const AdminSuppliers = lazy(() => import('@/pages/admin/AdminSuppliers'));
const AdminSites = lazy(() => import('@/pages/admin/AdminSites'));
const AdminBackup = lazy(() => import('@/pages/admin/AdminBackup'));
const AdminBackupArchives = lazy(() => import('@/pages/admin/AdminBackupArchives'));
const AdminAuditJournal = lazy(() => import('@/pages/admin/AdminAuditJournal'));
const AdminLoginJournal = lazy(() => import('@/pages/admin/AdminLoginJournal'));
const AdminVideoTutorials = lazy(() => import('@/pages/admin/AdminVideoTutorials'));
const AdminMenuStructure = lazy(() => import('@/pages/admin/AdminMenuStructure'));
const AdminDocuments = lazy(() => import('@/pages/admin/AdminDocuments'));
const AdminActualites = lazy(() => import('@/pages/admin/AdminActualites'));
const AdminJeux = lazy(() => import('@/pages/admin/AdminJeux'));
const AdminMicrosoftAccounts = lazy(() => import('@/pages/admin/AdminMicrosoftAccounts'));
const AdminMicrosoftFieldMappings = lazy(() => import('@/pages/admin/AdminMicrosoftFieldMappings'));
const AdminMicrosoftReferenceMappings = lazy(() => import('@/pages/admin/AdminMicrosoftReferenceMappings'));
const AdminMicrosoftCountryMappings = lazy(() => import('@/pages/admin/AdminMicrosoftCountryMappings'));
const AdminMicrosoftLicenses = lazy(() => import('@/pages/admin/AdminMicrosoftLicenses'));
const AdminMicrosoftTenant = lazy(() => import('@/pages/admin/AdminMicrosoftTenant'));

function AdminRouteFallback() {
  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="spinner" />
    </div>
  );
}

const PrivateRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="spinner" />
      </div>
    );
  }
  
  return user ? children : <Navigate to="/login" />;
};

// Route protégée générique pour toute page d'administration
// Vérifie les permissions granulaires
const AdminRoute = ({ children }) => {
  const { user, token, loading } = useAuth();
  const [granularPermissions, setGranularPermissions] = useState(null);
  const [permissionsLoading, setPermissionsLoading] = useState(true);
  
  useEffect(() => {
    const loadPermissions = async () => {
      if (!user?.id || user?.is_admin) {
        setPermissionsLoading(false);
        return;
      }
      
      try {
        const headers = {};
        if (token) headers.Authorization = `Bearer ${token}`;
        const data = await fetchGranularPermissions(user.id, { headers });
        setGranularPermissions(data);
      } catch (error) {
        console.error('Erreur chargement permissions:', error);
      } finally {
        setPermissionsLoading(false);
      }
    };
    
    if (user) {
      loadPermissions();
    }
  }, [user, token]);
  
  if (loading || permissionsLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="spinner" />
      </div>
    );
  }
  
  if (!user) return <Navigate to="/login" />;
  
  // Admin a toujours accès
  if (user.is_admin) return children;
  
  // Vérifier les permissions granulaires
  const hasAnyPermission = granularPermissions?.sections?.some(section => {
    return section.can_create || section.can_delete || 
      section.tabs?.some(tab => tab.can_read || tab.can_edit);
  });
  
  if (!hasAnyPermission) return <Navigate to="/" />;
  
  return children;
};

/** Enveloppe AdminRoute + Suspense pour le chunk de la page admin. */
function LazyAdminPage({ children }) {
  return (
    <AdminRoute>
      <Suspense fallback={<AdminRouteFallback />}>
        {children}
      </Suspense>
    </AdminRoute>
  );
}

function AppContent() {
  const { isHelpPanelOpen } = useVideoTutorial();
  const { user } = useAuth();
  const [pendingDocsModalOpen, setPendingDocsModalOpen] = React.useState(false);
  const [docsModalOpen, setDocsModalOpen] = React.useState(false);

  useEffect(() => {
    const root = document.getElementById('root');
    if (!root) return;
    if (isHelpPanelOpen) {
      root.style.transition = 'margin-right 0.28s ease';
      root.style.marginRight = HELP_PANEL_RESERVE_CSS;
    } else {
      root.style.marginRight = '';
      root.style.transition = '';
    }
    return () => {
      root.style.marginRight = '';
      root.style.transition = '';
    };
  }, [isHelpPanelOpen]);

  return (
    <div className="app-container">
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route
          path="/"
          element={
            <PrivateRoute>
              <Home />
            </PrivateRoute>
          }
        />
        <Route
          path="/vitrine"
          element={
            <PrivateRoute>
              <Vitrine />
            </PrivateRoute>
          }
        />
        <Route
          path="/aide/tutoriel/:tutorialId"
          element={
            <PrivateRoute>
              <HelpTutorialWindow />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin"
          element={
            <AdminRoute>
              <Navigate to="/admin/users" replace />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/users"
          element={
            <LazyAdminPage>
              <AdminLayout>
                <AdminUsers />
              </AdminLayout>
            </LazyAdminPage>
          }
        />
        <Route
          path="/admin/services"
          element={
            <LazyAdminPage>
              <AdminLayout>
                <AdminServices />
              </AdminLayout>
            </LazyAdminPage>
          }
        />
        <Route
          path="/admin/companies"
          element={
            <LazyAdminPage>
              <AdminLayout>
                <AdminCompanies />
              </AdminLayout>
            </LazyAdminPage>
          }
        />
        <Route
          path="/admin/applications"
          element={
            <LazyAdminPage>
              <AdminLayout>
                <AdminApplications />
              </AdminLayout>
            </LazyAdminPage>
          }
        />
        <Route
          path="/admin/suppliers"
          element={
            <LazyAdminPage>
              <AdminLayout>
                <AdminSuppliers />
              </AdminLayout>
            </LazyAdminPage>
          }
        />
        <Route
          path="/admin/entreprises"
          element={
            <LazyAdminPage>
              <AdminLayout>
                <AdminSites />
              </AdminLayout>
            </LazyAdminPage>
          }
        />
        <Route
          path="/admin/backup"
          element={
            <AdminRoute>
              <Navigate to="/admin/backup/exports" replace />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/backup/exports"
          element={
            <LazyAdminPage>
              <AdminLayout>
                <AdminBackup />
              </AdminLayout>
            </LazyAdminPage>
          }
        />
        <Route
          path="/admin/backup/archives"
          element={
            <LazyAdminPage>
              <AdminLayout>
                <AdminBackupArchives />
              </AdminLayout>
            </LazyAdminPage>
          }
        />
        <Route
          path="/admin/backup/audit-journal"
          element={
            <LazyAdminPage>
              <AdminLayout>
                <AdminAuditJournal />
              </AdminLayout>
            </LazyAdminPage>
          }
        />
        <Route
          path="/admin/backup/login-journal"
          element={
            <LazyAdminPage>
              <AdminLayout>
                <AdminLoginJournal />
              </AdminLayout>
            </LazyAdminPage>
          }
        />
        <Route
          path="/admin/tutorials"
          element={
            <LazyAdminPage>
              <AdminLayout>
                <AdminVideoTutorials />
              </AdminLayout>
            </LazyAdminPage>
          }
        />
        <Route
          path="/admin/menu-structure"
          element={
            <LazyAdminPage>
              <AdminLayout>
                <AdminMenuStructure />
              </AdminLayout>
            </LazyAdminPage>
          }
        />
        <Route
          path="/admin/api/microsoft/accounts"
          element={
            <LazyAdminPage>
              <AdminLayout>
                <AdminMicrosoftAccounts />
              </AdminLayout>
            </LazyAdminPage>
          }
        />
        <Route
          path="/admin/api/microsoft/field-mappings"
          element={
            <LazyAdminPage>
              <AdminLayout>
                <AdminMicrosoftFieldMappings />
              </AdminLayout>
            </LazyAdminPage>
          }
        />
        <Route
          path="/admin/api/microsoft/reference-mappings"
          element={
            <LazyAdminPage>
              <AdminLayout>
                <AdminMicrosoftReferenceMappings />
              </AdminLayout>
            </LazyAdminPage>
          }
        />
        <Route
          path="/admin/api/microsoft/country-mappings"
          element={
            <LazyAdminPage>
              <AdminLayout>
                <AdminMicrosoftCountryMappings />
              </AdminLayout>
            </LazyAdminPage>
          }
        />
        <Route
          path="/admin/api/microsoft/licenses"
          element={
            <LazyAdminPage>
              <AdminLayout>
                <AdminMicrosoftLicenses />
              </AdminLayout>
            </LazyAdminPage>
          }
        />
        <Route
          path="/admin/api/microsoft/tenant"
          element={
            <LazyAdminPage>
              <AdminLayout>
                <AdminMicrosoftTenant />
              </AdminLayout>
            </LazyAdminPage>
          }
        />
        <Route
          path="/admin/api"
          element={
            <AdminRoute>
              <Navigate to="/admin/api/microsoft/accounts" replace />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/vie-du-groupe/documents"
          element={
            <LazyAdminPage>
              <AdminLayout>
                <AdminDocuments />
              </AdminLayout>
            </LazyAdminPage>
          }
        />
        <Route
          path="/admin/vie-du-groupe/actualites"
          element={
            <LazyAdminPage>
              <AdminLayout>
                <AdminActualites />
              </AdminLayout>
            </LazyAdminPage>
          }
        />
        <Route
          path="/admin/vie-du-groupe/jeux"
          element={
            <LazyAdminPage>
              <AdminLayout>
                <AdminJeux />
              </AdminLayout>
            </LazyAdminPage>
          }
        />
        <Route
          path="/admin/vie-du-groupe/jeux/:seasonId"
          element={
            <LazyAdminPage>
              <AdminLayout>
                <AdminJeux />
              </AdminLayout>
            </LazyAdminPage>
          }
        />
        <Route
          path="/admin/vie-du-groupe"
          element={
            <AdminRoute>
              <Navigate to="/admin/vie-du-groupe/documents" replace />
            </AdminRoute>
          }
        />
        <Route path="/maintenance" element={<Maintenance />} />
      </Routes>

      <Toaster 
        position="bottom-center"
        toastOptions={{
          style: {
            zIndex: 9999,
          },
        }}
      />
      
      {/* Panneau d'aide - Tutoriels vidéo */}
      <HelpPanel />

      {/* Modal de bienvenue à la 1ère connexion */}
      <WelcomeModal />
      <VersionWhatsNewModal />

      {/* Sidebar d'actualités — masquée quand le Centre d'aide est ouvert */}
      {user && <NewsSidebar hidden={isHelpPanelOpen} />}

      {/* Modal post-connexion : documents en attente */}
      {user && (
        <PendingDocumentsModal
          onOpenDocuments={() => {
            openPrysmeAccessApp({ API: config.BACKEND_URL, user }).catch((err) => {
              console.error('Ouverture Prysme Access:', err);
              window.dispatchEvent(new CustomEvent('open-prysme-access-documents'));
            });
          }}
        />
      )}
    </div>
  );
}

function App() {
  useEffect(() => {
    // Supprimer l'erreur ResizeObserver qui est bénigne mais agaçante
    const resizeObserverErrHandler = (e) => {
      if (e.message === 'ResizeObserver loop completed with undelivered notifications.' ||
          e.message === 'ResizeObserver loop limit exceeded') {
        e.stopImmediatePropagation();
        e.preventDefault();
        return true;
      }
    };
    
    window.addEventListener('error', resizeObserverErrHandler);
    
    return () => {
      window.removeEventListener('error', resizeObserverErrHandler);
    };
  }, []);

  return (
    <BrowserRouter>
      <DbModeProvider>
        <AuthProvider>
          <ThemeProvider>
            <VideoTutorialProvider>
              <GroupProvider>
                <DevModeBanner />
                <AppContent />
              </GroupProvider>
            </VideoTutorialProvider>
            <DevBadge />
          </ThemeProvider>
        </AuthProvider>
      </DbModeProvider>
    </BrowserRouter>
  );
}

export default App;
